import React, { ReactElement, useMemo } from 'react';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import { useGetLocationBreakdown, OverviewLocationFilters, LocationBreakdownRow } from '@Apis/companyDashboard';
import { TASK_FILTER_ID_FIELDS } from '../filterFields';
import { BreakdownView, BreakdownMetric } from './breakdownSelection';
import LocationBreakdownTable from './LocationBreakdownTable';
import './LocationBreakdownPanel.scss';

export type { BreakdownView };

export const DEFAULT_BREAKDOWN_VIEW: BreakdownView = 'discipline';

// One entry per switcher button. `noun`/`singular` are the words used in the panel subtext
// ("Showing 5 Disciplines") and the empty state, so the wording can never drift from the
// selected button. `id` doubles as the endpoint's `groupBy` value.
export const BREAKDOWN_VIEWS: { id: BreakdownView; label: string; noun: string; singular: string }[] = [
    { id: 'department', label: 'By Department', noun: 'Departments', singular: 'Department' },
    { id: 'discipline', label: 'By Discipline', noun: 'Disciplines', singular: 'Discipline' },
    { id: 'phase', label: 'By Phase', noun: 'Phases', singular: 'Phase' },
    { id: 'tag', label: 'By Tag', noun: 'Tags', singular: 'Tag' },
];

// A subset of the shared TASK_FILTER_ID_FIELDS split (see filterFields.ts): `projectIds` is
// excluded because Openings always sends it non-empty (the tab's own project scope, falling back
// from the user's Projects filter — see Openings.tsx's `locationFilters`), so treating it as a
// "task filter" would make `hasActiveTaskFilters` true on every render. `tagIds` is excluded
// because tags are a Location filter, not a Task filter, and never affect this panel.
const TASK_FILTER_FIELDS: Array<keyof OverviewLocationFilters> =
    TASK_FILTER_ID_FIELDS.filter((field) => field !== 'projectIds');

const hasActiveTaskFilters = (filters: OverviewLocationFilters): boolean =>
    filters.isMilestonesOnly === true ||
    TASK_FILTER_FIELDS.some((field) => ((filters[field] as number[] | undefined)?.length ?? 0) > 0);

// Below this many rows the list is easy to scan as-is; above it, an unfiltered list is worth
// a nudge toward the filters that would shrink it.
const NUDGE_ROW_THRESHOLD = 20;

interface LocationBreakdownPanelProps {
    locationName: string;
    locationKey: string;
    view: BreakdownView;
    onViewChange: (view: BreakdownView) => void;
    onClose: () => void;
    isExpanded: boolean;
    /** Which configurable Overview tab this panel is nested under, so project scoping matches. */
    tabId: number;
    /** The page's active filters, so the panel counts the tasks its location row counts. */
    filters: OverviewLocationFilters;
    /** Drills into one group's tasks in the sidebar, narrowed to the clicked value. */
    onGroupSelect: (row: LocationBreakdownRow, metric: BreakdownMetric) => void;
    /** Opens the page's filters drawer (the toolbar's "Filters" button) — the "filters" link and the row-count nudge both use it. */
    onOpenFilters?: () => void;
}

const LocationBreakdownPanel = ({
    locationName,
    locationKey,
    view,
    onViewChange,
    onClose,
    isExpanded,
    tabId,
    filters,
    onGroupSelect,
    onOpenFilters,
}: LocationBreakdownPanelProps): ReactElement => {

    const activeView = BREAKDOWN_VIEWS.find((candidate) => candidate.id === view) ?? BREAKDOWN_VIEWS[0];

    // Gated on `isExpanded`, not on being mounted: the panel stays mounted for the length of
    // the collapse animation, and a row on its way out should not start a fetch. Switching the
    // view switcher changes the query key, so the table re-fetches and re-renders on its own.
    const { data, isLoading, error } = useGetLocationBreakdown(locationKey, view, tabId, filters, isExpanded);

    // `data !== undefined` — not `!isLoading` — is what says "a response has landed".
    // Collapsing a row mid-flight disables the query, and a query disabled before it ever
    // resolved reports `isLoading: false` with no data and no error; gating the empty state on
    // that would tell the user this location has no breakdown without ever having asked.
    const hasLoaded = data !== undefined;
    const rows = data?.groups ?? [];
    const hasRows = rows.length > 0;
    const hasCounts = hasLoaded && !error;

    // Tag rows come from tagIds, a Location filter (see TASK_FILTER_FIELDS above), so the Tag
    // view is never affected by task filters and never shows the "X of Y matching" phrasing.
    const isTagView = view === 'tag';
    const hasTaskFilters = hasActiveTaskFilters(filters);
    const showMatchingCount = hasTaskFilters && !isTagView;

    // The endpoint only ever returns the *filtered* groups, so the unfiltered total ("of Y")
    // is fetched as a second, separately-keyed query with the task filters reset — everything
    // else (location scope, tagIds) is left alone, since it doesn't affect which groups exist.
    const unfilteredFilters = useMemo(() => {
        const reset = Object.fromEntries(TASK_FILTER_FIELDS.map((field) => [field, []]));
        return { ...filters, ...reset, isMilestonesOnly: false };
    }, [filters]);
    const { data: totalData } = useGetLocationBreakdown(locationKey, view, tabId, unfilteredFilters, isExpanded && showMatchingCount);
    const totalCount = totalData?.groups.length;
    const hasTotalCount = totalCount !== undefined;

    const nounFor = (count: number) => (count === 1 ? activeView.singular : activeView.noun);

    return (
        <div className='location-breakdown-panel' aria-hidden={!isExpanded}>

            <div className='lbp-header'>

                <div className='lbp-header__text'>
                    <div className='lbp-title'>Breakdown for {locationName}</div>
                    <div className='lbp-subtitle'>
                        {!hasCounts && <>Showing — {activeView.noun}</>}

                        {hasCounts && (isTagView || !hasTaskFilters) && (
                            <>
                                Showing all {rows.length} {nounFor(rows.length)}
                                {!hasTaskFilters && rows.length > NUDGE_ROW_THRESHOLD && onOpenFilters && (<>
                                    {' · '}
                                    <button
                                        type='button'
                                        className='lbp-subtitle-link'
                                        tabIndex={isExpanded ? undefined : -1}
                                        onClick={onOpenFilters}>    Tip: use filters to narrow this list
                                    </button>
                                </>
                                )}
                            </>
                        )}

                        {hasCounts && showMatchingCount && hasTotalCount && (
                            <>
                                Showing {rows.length} of {totalCount} {nounFor(rows.length)} matching your active{' '}
                                <button type='button' className='lbp-subtitle-link' onClick={onOpenFilters}>
                                    filters
                                </button>
                            </>
                        )}

                        {hasCounts && showMatchingCount && !hasTotalCount && (
                            <>Showing {rows.length} {nounFor(rows.length)}</>
                        )}
                    </div>
                </div>

                <div className='lbp-header__actions'>

                    <div className='lbp-switcher' role='group' aria-label='Breakdown view'>
                        {BREAKDOWN_VIEWS.map((candidate) => (
                            <button
                                key={candidate.id}
                                type='button'
                                className={`lbp-switcher__btn${candidate.id === view ? ' lbp-switcher__btn--active' : ''}`}
                                aria-pressed={candidate.id === view}
                                tabIndex={isExpanded ? undefined : -1}
                                onClick={() => onViewChange(candidate.id)}>
                                {candidate.label}
                            </button>
                        ))}
                    </div>

                    <button
                        type='button'
                        className='lbp-close'
                        aria-label='Close breakdown'
                        title='Close breakdown'
                        tabIndex={isExpanded ? undefined : -1}
                        onClick={onClose}>
                        {/* Solid, not regular: the bundled regular-400 subset has no xmark
                            and renders a tofu box. Matches the location detail drawer's
                            close button, which is already `fas fa-xmark`. */}
                        <i className='fa-solid fa-xmark' />
                    </button>

                </div>

            </div>

            {error && (
                <div className='lbp-message lbp-message--error'>
                    Unable to load the breakdown for this location.
                </div>
            )}

            {!error && (
                <LoadingIndicator isLoading={isLoading} hideWhileLoading>
                    {hasRows && (
                        <LocationBreakdownTable
                            key={view}
                            rows={rows}
                            groupLabel={activeView.singular}
                            onGroupSelect={onGroupSelect}
                        />
                    )}
                    {hasLoaded && !hasRows && (
                        <div className='lbp-message'>
                            No {activeView.singular} data available for this location
                        </div>
                    )}
                </LoadingIndicator>
            )}

        </div>
    );
};

export default LocationBreakdownPanel;
